<script lang="ts" setup>
import { CostCenter } from '@/core/collections/contracts-api/companies/invoice-groups/cost-centers/cost-center.interface';
import { InvoiceGroup } from '@/core/collections/contracts-api/companies/invoice-groups/invoice-group.interface';
import { Shift } from '@/core/collections/contracts-api/companies/shifts/shift.interface';
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import { Worksheet } from '@/core/collections/contracts-api/customers/employees/contract-extensions/contract-extension.interface';
import Dropdown from '@/core/components/form-elements/Dropdown.vue';
import { shortDays } from '@/core/helpers/dateHelper';
import { NxtInput } from '@nxtpeople/nxt-styles';
import { computed, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWorkCycle } from './useWorkCycle';

//#region Globals
const { t, locale } = useI18n();
const { mapTimetables } = useWorkCycle();
//#endregion

//#region Props & Emits
const emits = defineEmits<{
   (e: 'onDuplicateWorksheet', worksheetIndex: number): void;
   (e: 'onDeleteWorksheet', worksheetIndex: number): void;
   (e: 'onUpdateWorksheet', payload: { key: string; value: any; worksheetIndex: number }): void;
}>();
const props = defineProps({
   worksheetIndex: { type: Number, default: 0 },
   worksheet: { type: Object as PropType<Worksheet>, default: null },
   customer: { type: Object as PropType<Customer>, required: true },
   errors: { type: Object, default: () => ({}) },
   loading: { type: Boolean, default: false },
   isInitializing: { type: Boolean, default: false },

   invoiceGroups: { type: Array as PropType<Array<InvoiceGroup>>, default: () => [] },
   costCenters: { type: Array as PropType<Array<CostCenter>>, default: () => [] },
   shifts: { type: Array as PropType<Array<Shift>>, default: () => [] },
});
const { worksheet, worksheetIndex } = toRefs(props);
//#endregion

//#region Open Worksheet
const isOpen = ref(true);
//#endregion

//#region Shift
const shift = computed(() => worksheet.value.shift);

function setShift(shift?: Shift) {
   if (shouldIgnoreInitializationUpdate(worksheet.value.shift_id, shift?.id)) return;
   emits('onUpdateWorksheet', { key: 'shift_id', value: shift?.id, worksheetIndex: worksheetIndex.value });
   emits('onUpdateWorksheet', { key: 'shift', value: shift, worksheetIndex: worksheetIndex.value });
}

function updateInvoiceGroup(value: any) {
   if (shouldIgnoreInitializationUpdate(worksheet.value.invoice_group_id, value)) return;
   emits('onUpdateWorksheet', { key: 'invoice_group_id', value, worksheetIndex: worksheetIndex.value });
}

function updateCostCenter(value: any) {
   if (shouldIgnoreInitializationUpdate(worksheet.value.cost_center_id, value)) return;
   emits('onUpdateWorksheet', { key: 'cost_center_id', value, worksheetIndex: worksheetIndex.value });
}

watch(shift, initShiftTimetable, { immediate: true });
function initShiftTimetable(shift?: Shift) {
   if (!shift) return;
   worksheet.value.timetables = mapTimetables(shift.timetables, true);
}
//#endregion

//#region Second Timing
const showSecondTiming = ref(false);
watch(worksheet, detectSecondTiming, { immediate: true, deep: true });
function detectSecondTiming(worksheet: Worksheet) {
   showSecondTiming.value = !!worksheet.timetables.find(
      (timetable) => timetable.second_start_time || timetable.second_stop_time || !!timetable.second_time_range,
   );
}
//#endregion

//#region Timetable
const days = computed(() => shortDays[locale.value]);

function setFromTill(key: string, value: string, dayIndex: number) {
   const timetables = [...(worksheet.value?.timetables || [])];
   const timetableIndex = timetables.findIndex((tt) => tt.weekday === dayIndex + 1);
   if (timetableIndex < 0) return;

   const normalizedValue = normalizeTimetableInputValue(value);
   const currentValue = (timetables[timetableIndex] as any)?.[key];
   if (isEquivalentValue(currentValue, normalizedValue)) return;

   timetables.splice(timetableIndex, 1, { ...timetables[timetableIndex], [key]: normalizedValue });
   emits('onUpdateWorksheet', { key: 'timetables', value: timetables, worksheetIndex: worksheetIndex.value });
}

function normalizeTimetableInputValue(value?: string) {
   if (typeof value !== 'string') return value;
   const trimmedValue = value.trim();
   if (!trimmedValue) return undefined;

   // Masked inputs can emit a fully empty placeholder while mounting.
   if (/^_{2}:_{2}\s-\s_{2}:_{2}$/.test(trimmedValue)) return undefined;
   return value;
}

function isEquivalentValue(currentValue: any, newValue: any) {
   const currentIsEmpty = currentValue === undefined || currentValue === null || currentValue === '';
   const newIsEmpty = newValue === undefined || newValue === null || newValue === '';
   if (currentIsEmpty && newIsEmpty) return true;
   return currentValue === newValue;
}

function shouldIgnoreInitializationUpdate(currentValue: any, newValue: any) {
   if (!props.isInitializing) return false;
   const currentIsEmpty = currentValue === undefined || currentValue === null || currentValue === '';
   const newIsEmpty = newValue === undefined || newValue === null || newValue === '';
   return currentIsEmpty && !newIsEmpty;
}
//#endregion
</script>

<template>
   <div :class="['contract-extension-worksheet', { '-isOpen': isOpen, '-next-to-extend': !!worksheet.next_to_extend }]">
      <!-- Drag Handle -->
      <div class="contract-extension-worksheet__handle">
         <button class="handle btn btn-icon" type="button">
            <i class="fas fa-grip-vertical"></i>
         </button>
         <div class="btn btn-icon mt-1">
            <i class="fas fa-arrow-circle-down"></i>
         </div>
      </div>

      <!-- Worksheet Content -->
      <div class="contract-extension-worksheet__content">
         <!-- Worksheet Heading -->
         <div class="contract-extension-worksheet__heading">
            <div class="contract-extension-worksheet__heading__left">
               <button type="button" class="btn btn-icon" @click="() => (isOpen = !isOpen)">
                  <i class="fas fa-chevron-down"></i>
               </button>
               <strong class="text-nowrap"> {{ worksheet?.shift?.name }} </strong>
            </div>

            <div class="contract-extension-worksheet__heading__right">
               <NxtIconButton
                  :name="'copy'"
                  :disabled="loading"
                  :hover-text="$t('actions.copy-type', { type: $t('work-cyles.worksheets.worksheet') })"
                  @on-click="emits('onDuplicateWorksheet', worksheetIndex)"
               />

               <NxtIconButton
                  :name="'trash'"
                  :disabled="loading"
                  :hover-text="$t('actions.delete-type', { type: $t('work-cyles.worksheets.worksheet') })"
                  @on-click="emits('onDeleteWorksheet', worksheetIndex)"
               />
            </div>
         </div>

         <!-- Worksheet Details -->
         <div class="contract-extension-worksheet__details">
            <div class="row">
               <Dropdown
                  :model-value="worksheet.invoice_group_id"
                  :class="'col-3'"
                  :name="`invoice_group_id-${worksheetIndex}`"
                  :label="$t('companies.invoice-groups.invoice-group', 1)"
                  :options="invoiceGroups"
                  :required="true"
                  :errors="errors"
                  :disabled="loading"
                  @update:model-value="updateInvoiceGroup"
               />
               <Dropdown
                  :model-value="worksheet.cost_center_id"
                  :class="'col-3'"
                  :name="`cost_center_id-${worksheetIndex}`"
                  :label="$t('companies.cost-centers.cost-center', 1)"
                  :options="costCenters"
                  :errors="errors"
                  :disabled="loading"
                  @update:model-value="updateCostCenter"
               />
               <Dropdown
                  :model-value="worksheet.shift_id"
                  :class="'col-3'"
                  :name="`shift_id-${worksheetIndex}`"
                  :label="$t('companies.shifts.shift', 1)"
                  :options="shifts"
                  :errors="errors"
                  :disabled="loading"
                  :emit-object="true"
                  @update:model-value="setShift"
               />
            </div>

            <table class="contract-extension-worksheet__shift">
               <tbody>
                  <!-- Day Headings -->
                  <tr>
                     <td></td>
                     <td v-for="(day, dayIndex) in days" :key="dayIndex">
                        {{ day }}
                     </td>
                  </tr>

                  <!-- First Time Range -->
                  <tr>
                     <td>{{ t('generic.dates.from-till') }}</td>

                     <td v-for="(day, dayIndex) in 7" :key="day">
                        <NxtInput
                           :model-value="worksheet?.timetables?.[dayIndex]?.first_time_range"
                           :name="`worksheet_${worksheetIndex}_first_time_range_${day}`"
                           :mask="'##:## - ##:##'"
                           :placeholder="'00:00 - 00:00'"
                           @update:model-value="setFromTill('first_time_range', $event, dayIndex)"
                        />
                     </td>
                  </tr>

                  <!-- Second Time Range -->
                  <tr v-if="!showSecondTiming">
                     <td>
                        <button class="btn btn-icon" @click="showSecondTiming = true">
                           <i class="fas fa-add"></i>
                        </button>
                     </td>
                     <td :colspan="7"></td>
                  </tr>
                  <tr v-else>
                     <td>{{ t('generic.dates.from-till') }}</td>

                     <td v-for="(day, dayIndex) in 7" :key="day">
                        <NxtInput
                           :model-value="worksheet?.timetables?.[dayIndex]?.second_time_range"
                           :name="`worksheet_${worksheetIndex}_second_time_range_${day}`"
                           :mask="'##:## - ##:##'"
                           :placeholder="'00:00 - 00:00'"
                           @update:model-value="setFromTill('second_time_range', $event, dayIndex)"
                        />
                     </td>
                  </tr>

                  <!-- Planned Hours -->
                  <tr>
                     <td>
                        <strong>{{ t('generic.total') }}</strong>
                     </td>
                     <td v-for="(day, dayIndex) in 7" :key="day">
                        <NxtInput
                           :model-value="worksheet?.timetables?.[dayIndex]?.planned_hours"
                           :name="`worksheet_${worksheetIndex}_planned_hours_${day}`"
                           :placeholder="t('generic.total')"
                           :step="0.01"
                           v-decimal
                           @update:model-value="setFromTill('planned_hours', $event, dayIndex)"
                        />
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
      </div>
   </div>
</template>

<style lang="scss">
.contract-extension-worksheet {
   background-color: var(--surface-container-1);
   border: toRem(1) solid var(--nxt-grey-200);
   border-radius: var(--nxt-radius-large);
   display: grid;
   grid-template-columns: toRem(38) 1fr;
   overflow: hidden;
   width: toRem(1051);

   .fa-chevron-down {
      transform: rotate(-90deg);
      transition: transform 0.3s ease-in-out;
   }

   &.-isOpen {
      .fa-chevron-down {
         transform: rotate(0deg);
      }
      .contract-extension-worksheet__details {
         max-height: toRem(250);
      }
   }

   &.-next-to-extend {
      border: toRem(1) solid var(--nxt-brand-primary-light);
   }
}

.contract-extension-worksheet__handle {
   background-color: var(--nxt-grey-extralight);
   padding: var(--nxt-gutter);
   display: flex;
   flex-direction: column;
   justify-content: space-between;
   height: 100%;

   .handle {
      cursor: grab;
   }
}

.contract-extension-worksheet__content {
   padding: var(--nxt-gutter);
   width: 100%;
}

.contract-extension-worksheet__heading {
   display: flex;
   align-items: center;
   justify-content: space-between;
   height: toRem(30);
}

.contract-extension-worksheet__heading__left {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter);
}

.contract-extension-worksheet__heading__right {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter);
}

.contract-extension-worksheet__details {
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);

   max-height: 0;
   overflow: hidden;
   transition: max-height 0.3s ease-in-out;
}

.contract-extension-worksheet__shift {
   border-spacing: var(--nxt-gutter-small);
   td {
      max-width: toRem(100);
      height: toRemx(36);

      &:not(:first-of-type) {
         text-align: center;
      }
   }
   input {
      text-align: center;
   }
}
</style>
